import React, { Dispatch, SetStateAction } from 'react';

import { useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { PaginationProps as AntdPaginationProps } from 'antd/lib/pagination/Pagination';
import { TableProps as AntdTableProps } from 'antd/lib/table/Table';

import { Pagination } from '../Pagination';
import SortableTable from '../SortableTable';
import {
  BuildItemKey,
  ColumnProps,
  FetchDataResult,
  TableHeaderFunction,
  TablePagination,
  TableSort,
} from '../types';

export interface TableQueryProps<
  DataType extends object,
  FilterType,
  SortType extends string | undefined,
> {
  // Table - general
  columns: ColumnProps<DataType, SortType>[];
  query: UseQueryResult<FetchDataResult<DataType>>;
  queryKeyLabel: string;
  emptyDescription?: React.ReactNode;
  // Header
  header?: TableHeaderFunction<DataType, FilterType, SortType>;
  // Pagination
  pageSizeOptions?: number[];
  pagination: TablePagination;
  setPagination: Dispatch<SetStateAction<TablePagination>>;
  // Other
  disabled?: boolean;
  antdTableProps?: Partial<AntdTableProps<DataType>>;
  antPaginationProps?: Partial<AntdPaginationProps>;
  //  filters
  filter: Partial<FilterType>;
  setFilter: Dispatch<SetStateAction<Partial<FilterType>>>;
  // sorts
  sort: Partial<TableSort<SortType>>;
  setSort: Dispatch<SetStateAction<Partial<TableSort<SortType>>>>;
  // Key builder
  buildItemKey?: BuildItemKey<DataType>;
}

function TableQuery<
  DataType extends object,
  FilterType,
  SortType extends string | undefined,
>({
  // Table - general
  columns,
  query,
  queryKeyLabel,
  emptyDescription,
  // Header
  header,
  // Pagination
  pageSizeOptions,
  pagination,
  setPagination,
  // Other
  disabled,
  antdTableProps,
  antPaginationProps,
  // filters
  filter,
  setFilter,

  // Sorts
  sort,
  setSort,
  // Key builder
  buildItemKey,
}: TableQueryProps<DataType, FilterType, SortType>): React.ReactElement {
  function innerSetPagination(newPagination: TablePagination): void {
    setPagination(newPagination);
  }

  // Go back to pagination when filter is update
  function innerSetFilter(filter: Partial<FilterType>): void {
    setPagination({ ...pagination, page: 1 });
    setFilter(filter);
  }

  const queryClient = useQueryClient();

  const fetchedData: FetchDataResult<DataType> = query.data ?? {
    data: [],
    total: 0,
  };

  return (
    <>
      {header && header(fetchedData, [], filter, innerSetFilter, sort)}
      <SortableTable
        data={fetchedData.data}
        columnsProps={columns}
        isLoading={query.isFetching}
        emptyDescription={emptyDescription}
        disabled={disabled}
        reload={() => {
          void queryClient.invalidateQueries({
            queryKey: [queryKeyLabel, filter, sort, pagination],
          });
        }}
        isOnError={query.isError}
        sortItems={{
          sorted: sort,
          setSort: setSort,
        }}
        buildItemKey={buildItemKey}
        antdTableProps={{
          ...antdTableProps,
          pagination: false,
        }} // We use our custom Pagination component instead
      />

      <Pagination
        pagination={{
          paginated: pagination,
          setPagination: innerSetPagination,
        }}
        total={fetchedData.total}
        pageSizeOptions={pageSizeOptions}
        disabled={disabled}
        antdPaginationProps={antPaginationProps}
      />
    </>
  );
}

export default TableQuery;
